import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, ExternalLink, MapPin, Pencil, Plus, Search } from 'lucide-react'
import { cmsApi } from '../lib/api'
import type { PlaceAmenities, PlaceCatalogItem } from '../types'
import { AMENITY_KEYS, AmenityPicker, isPending } from './AmenityPicker'
import { CatalogDrafts } from './CatalogDrafts'
import { CatalogNotice } from './CatalogNotice'
import { LoadingState } from './LoadingState'

type Review = 'pending' | 'checked' | 'all'

type Props = {
  canEdit: boolean
  catalogEditing: boolean
  // The filter it opens with, from the Visão geral.
  initialReview?: 'pending'
  initialProfile?: 'missing'
  onCreate: () => void
  onOpenPlace: (id: string) => void
  onOpenDraft: (id: string) => void
  onSaved: (message: string) => void
  onError: (message: string) => void
}

const pickAmenities = (place: PlaceCatalogItem): PlaceAmenities =>
  Object.fromEntries(AMENITY_KEYS.map((key) => [key, place[key] ?? null])) as PlaceAmenities

// Maps URLs open the place in Google Maps, the app or the site, with no key.
const mapsUrl = (place: PlaceCatalogItem) => {
  const query = [place.name, place.address || place.city].filter(Boolean).join(', ')
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

export function PlacesPage({ canEdit, catalogEditing, initialReview, initialProfile, onCreate, onOpenPlace, onOpenDraft, onSaved, onError }: Props) {
  const [items, setItems] = useState<PlaceCatalogItem[]>([])
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // Every place first; what is still to check is a filter (PRD.md, item 98).
  const [review, setReview] = useState<Review>(initialReview ?? 'all')
  // Places still without their venue profile, the app's page for them.
  const [profile, setProfile] = useState<'all' | 'missing' | 'linked'>(initialProfile ?? 'all')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const pageSize = 20

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => setPage(1), [debouncedSearch, review, profile])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize), review })
      if (debouncedSearch) params.set('search', debouncedSearch)
      if (profile !== 'all') params.set('profile', profile)
      const result = await cmsApi.listPlaces(params)
      setItems(result.items)
      setTotal(result.total)
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar os lugares.')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, review, profile])

  useEffect(() => { void load() }, [load])

  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const replace = (place: PlaceCatalogItem) =>
    setItems((current) => current.map((item) => (item.id === place.id ? place : item)))

  return (
    <div className="page-stack">
      <section className="page-heading page-heading--with-action">
        <div>
          <h1>Lugares</h1>
          <p>Confira o que cada lugar oferece. O app mostra só o que estiver marcado como Sim.</p>
        </div>
        {canEdit && catalogEditing && <button className="button button--primary" onClick={onCreate}><Plus size={18} /> Novo lugar</button>}
      </section>

      <CatalogNotice catalogEditing={catalogEditing} />

      {canEdit && catalogEditing && <CatalogDrafts kind="place" onOpen={onOpenDraft} onNotice={onSaved} onError={onError} />}

      <section className="panel panel--flush">
        <div className="filter-bar filter-bar--places">
          <label className="search-field">
            <Search size={18} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome" />
          </label>
          <select aria-label="Conferência" value={review} onChange={(event) => setReview(event.target.value as Review)}>
            <option value="all">Todos os lugares</option>
            <option value="pending">Falta conferir</option>
            <option value="checked">Conferidos</option>
          </select>
          <select aria-label="Perfil" value={profile} onChange={(event) => setProfile(event.target.value as typeof profile)}>
            <option value="all">Com e sem perfil</option>
            <option value="missing">Sem perfil</option>
            <option value="linked">Com perfil</option>
          </select>
        </div>

        {loading ? <LoadingState label="Carregando lugares…" /> : error ? (
          <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button></div>
        ) : items.length ? (
          <div className="place-list">
            {items.map((place) => (
              <PlaceCard key={place.id} place={place} canEdit={canEdit} onOpen={() => onOpenPlace(place.id)} onSaved={(saved) => { replace(saved); onSaved('Lugar atualizado.') }} onError={onError} />
            ))}
          </div>
        ) : (
          <div className="empty-state"><MapPin size={30} /><h3>Nenhum lugar encontrado</h3><p>{review === 'pending' ? 'Nada falta conferir com esses filtros.' : 'Revise a busca ou os filtros.'}</p></div>
        )}

        <div className="pagination">
          <span>{total.toLocaleString('pt-BR')} {total === 1 ? 'lugar' : 'lugares'}</span>
          <div>
            <button className="icon-button" aria-label="Página anterior" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft size={18} /></button>
            <span>{page} de {pageCount}</span>
            <button className="icon-button" aria-label="Próxima página" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}><ChevronRight size={18} /></button>
          </div>
        </div>
      </section>
    </div>
  )
}

type CardProps = {
  place: PlaceCatalogItem
  canEdit: boolean
  onOpen: () => void
  onSaved: (place: PlaceCatalogItem) => void
  onError: (message: string) => void
}

function PlaceCard({ place, canEdit, onOpen, onSaved, onError }: CardProps) {
  const saved = pickAmenities(place)
  const [draft, setDraft] = useState<PlaceAmenities>(saved)
  const [saving, setSaving] = useState(false)
  const changed = AMENITY_KEYS.filter((key) => draft[key] !== saved[key])
  const pending = isPending(draft)

  const save = async () => {
    setSaving(true)
    try {
      const result = await cmsApi.updatePlaceAmenities(
        place.id,
        Object.fromEntries(changed.map((key) => [key, draft[key]])),
      )
      const updated = { ...place, ...result.amenities }
      setDraft(pickAmenities(updated))
      onSaved(updated)
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar o lugar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <article className="place-card" aria-label={place.name}>
      <header className="place-card__heading">
        <div>
          <strong>{place.name}</strong>
          <span>{[place.address, place.city].filter(Boolean).join(' · ') || 'Sem endereço'}</span>
        </div>
        <div className="place-card__badges">
          {place.has_upcoming_events && <span className="status-pill status-pill--published">Com eventos</span>}
          {place.has_profile === false && <span className="status-pill status-pill--deleted">Sem perfil</span>}
          <span className={`status-pill ${pending ? 'status-pill--canceled' : 'status-pill--published'}`}>{pending ? 'Falta conferir' : 'Conferido'}</span>
          <a className="text-button" href={mapsUrl(place)} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Abrir no Google Maps</a>
          <button className="text-button" onClick={onOpen}><Pencil size={15} /> Dados do lugar</button>
        </div>
      </header>

      <AmenityPicker name={place.id} value={draft} disabled={!canEdit || saving} onChange={setDraft} />

      {canEdit && (
        <footer className="place-card__footer">
          <span>{changed.length ? `${changed.length} ${changed.length === 1 ? 'alteração' : 'alterações'} sem salvar` : 'Tudo salvo'}</span>
          <div>
            <button className="button button--quiet" disabled={!changed.length || saving} onClick={() => setDraft(saved)}>Desfazer</button>
            <button className="button button--primary" disabled={!changed.length || saving} onClick={() => void save()}>{saving ? 'Salvando…' : 'Salvar'}</button>
          </div>
        </footer>
      )}
    </article>
  )
}
